import { useMutation, useQuery, type UseQueryOptions } from 'react-query'

import queryClient from '../../queryClient'
import { type ApiResponse, HTTPMethod, request } from '../api/legacyApi'

import { getLiveId } from './roomInfo'
import { getLiveToken } from './useConnectInfo'

type LiveDataFlag = boolean | 0 | 1

type LiveDataDTO = {
  before_webinar_countdown_p: LiveDataFlag
  broadcasting_p: LiveDataFlag
  cancelled_p: LiveDataFlag
  comment_mode: string
  question_mode: string
  draft_p: LiveDataFlag
  token: string
  duration_minutes: string
  live_format: string
  live_id: string
  live_label: string
  live_status: string
  locale: string
  name: string
  number_of_comments: string
  number_of_comments_fmt: string
  primary_timezone: string
  primary_timezone_live_date__date: string
  primary_timezone_live_date__time: string
  template_p: LiveDataFlag
  description_html: string
  allow_guest_speakers_p: LiveDataFlag
  streaming_method: string
  live_series_id: string
  live_series_token: string
  live_date_epoch: number
  thumbnail_small_download: string
  description_text: string
  enable_ondemand_p: LiveDataFlag
  after_webinar_link: string
  after_webinar_message: string
  after_webinar_headline: string
  after_webinar_photo_url: string
  after_webinar_photo_id: string
  after_webinar_photo_token: string
  after_webinar_photo_type?: string
  before_webinar_photo_url: string
  before_webinar_photo_id: string
  before_webinar_photo_token: string
  before_webinar_photo_type?: string
  webinar_design_id: number
}

type LiveData = {
  before_webinar_countdown_p: boolean
  broadcasting_p: boolean
  cancelled_p: boolean
  comment_mode: string
  question_mode: string
  draft_p: boolean
  token: string
  duration_minutes: string
  live_format: string
  live_id: string
  live_label: string
  live_status: string
  locale: string
  name: string
  number_of_comments: string
  number_of_comments_fmt: string
  primary_timezone: string
  primary_timezone_live_date__date: string
  primary_timezone_live_date__time: string
  template_p: boolean
  description_html: string
  allow_guest_speakers_p: boolean
  streaming_method: string
  live_series_id: string
  live_series_token: string
  live_date_epoch: number
  thumbnail_small_download: string
  description_text: string
  enable_ondemand_p: boolean
  after_webinar_link: string
  after_webinar_message: string
  after_webinar_headline: string
  after_webinar_photo_url: string
  after_webinar_photo_id: string
  after_webinar_photo_token: string
  after_webinar_photo_type?: string
  before_webinar_photo_url: string
  before_webinar_photo_id: string
  before_webinar_photo_token: string
  before_webinar_photo_type?: string
  webinar_design_id: number
}

const LIVE_DATA_QUERY_KEY = ['live_data'] as const

const toBoolean = (value: LiveDataFlag | undefined | null): boolean =>
  Boolean(value)

const mapDtoToLiveData = (dto: LiveDataDTO): LiveData => ({
  ...dto,
  before_webinar_countdown_p: toBoolean(dto.before_webinar_countdown_p),
  broadcasting_p: toBoolean(dto.broadcasting_p),
  cancelled_p: toBoolean(dto.cancelled_p),
  draft_p: toBoolean(dto.draft_p),
  template_p: toBoolean(dto.template_p),
  allow_guest_speakers_p: toBoolean(dto.allow_guest_speakers_p),
  enable_ondemand_p: toBoolean(dto.enable_ondemand_p),
})

const liveDataQuery = {
  queryKey: LIVE_DATA_QUERY_KEY,
  queryFn: async () => {
    const liveId = getLiveId()
    const params = {
      live_id: `${liveId}`,
      include_stats_p: true,
      include_webinar_images_p: true,
      token: await getLiveToken(),
    }

    const response = await request<ApiResponse<LiveDataDTO[]>>({
      url: '/api/2/live/list',
      method: HTTPMethod.GET,
      data: params,
    })

    const dto = response.data.at(0)

    if (!dto) {
      return undefined
    }

    return mapDtoToLiveData(dto)
  },
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchInterval: (liveData) => (liveData?.broadcasting_p ? 5000 : 2000),
} satisfies UseQueryOptions<LiveData | undefined>

const fetchLiveData = async () => {
  return queryClient.fetchQuery(liveDataQuery)
}

// Non-hook read of the already-fetched live data, without triggering a fetch
const getLiveData = () =>
  queryClient.getQueryData<LiveData | undefined>(LIVE_DATA_QUERY_KEY)

// Invokes the callback with the current live data and again whenever the
// query cache entry changes. Returns an unsubscribe function.
const subscribeToLiveData = (
  callback: (liveData: LiveData | undefined) => void
) => {
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (!event?.query || event.query.queryKey[0] !== LIVE_DATA_QUERY_KEY[0])
      return
    callback(getLiveData())
  })
  callback(getLiveData())
  return unsubscribe
}

const useLiveDataQuery = () => {
  return useQuery(liveDataQuery)
}

const useUpdateLiveDataMutation = () => {
  return useMutation({
    mutationFn: async (data: Partial<LiveData>) => {
      const liveId = getLiveId()
      const liveToken = await getLiveToken()

      const response = await request<ApiResponse<LiveDataDTO>>({
        url: '/api/2/live/update',
        method: HTTPMethod.POST,
        data: {
          live_id: liveId.toString(),
          token: liveToken,
          live_format: 'webinar',
          ...data,
        },
      })

      return mapDtoToLiveData(response.data)
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries(LIVE_DATA_QUERY_KEY)
      const previousData =
        queryClient.getQueryData<LiveData>(LIVE_DATA_QUERY_KEY)

      if (previousData) {
        queryClient.setQueryData(LIVE_DATA_QUERY_KEY, {
          ...previousData,
          ...data,
        })
      }

      return { previousData }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(LIVE_DATA_QUERY_KEY, context.previousData)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries(LIVE_DATA_QUERY_KEY)
    },
  })
}

const useUpdateOnCameraState = () => {
  const { mutate: update } = useUpdateLiveDataMutation()

  return (allowOnCamera: boolean) =>
    update({ allow_guest_speakers_p: allowOnCamera })
}

const useUpdateQuestionMode = () => {
  const { mutate: update } = useUpdateLiveDataMutation()

  return (isQuestionsEnabled: boolean) =>
    update({ question_mode: isQuestionsEnabled ? 'ask' : 'none' })
}

export {
  type LiveData,
  type LiveDataDTO,
  mapDtoToLiveData,
  useLiveDataQuery,
  fetchLiveData,
  getLiveData,
  subscribeToLiveData,
  useUpdateLiveDataMutation,
  useUpdateOnCameraState,
  useUpdateQuestionMode,
}
